import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { SortingDirection } from 'api/clients/FilesViewsClient'
import { Property, PropertyType } from 'api/clients/PropertyClient'
import { Button } from 'components/Button'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import Styles from './DirectionPicker.module.scss'

import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'

type SortDirection = {
  [key in SortingDirection]: string
}

const SortAlphabeticalDirection: SortDirection = {
  [SortingDirection.Ascending]: 'A-Z',
  [SortingDirection.Descending]: 'Z-A',
}

const FirstLastDirection: SortDirection = {
  [SortingDirection.Ascending]: 'First-Last',
  [SortingDirection.Descending]: 'Last-First',
}

const CheckboxDirection: SortDirection = {
  [SortingDirection.Ascending]: '[ ] - [✔️]',
  [SortingDirection.Descending]: '[✔️] - [ ]',
}

const NumericDirection: SortDirection = {
  [SortingDirection.Ascending]: '1-9',
  [SortingDirection.Descending]: '9-1',
}

const Directions = {
  [PropertyType.Title]: SortAlphabeticalDirection,
  [PropertyType.Text]: SortAlphabeticalDirection,
  [PropertyType.SingleSelect]: FirstLastDirection,
  [PropertyType.MultiSelect]: SortAlphabeticalDirection,
  [PropertyType.Date]: FirstLastDirection,
  [PropertyType.Document]: SortAlphabeticalDirection,
  [PropertyType.Person]: SortAlphabeticalDirection,
  [PropertyType.Checkbox]: CheckboxDirection,
  [PropertyType.Number]: NumericDirection,
  [PropertyType.Link]: SortAlphabeticalDirection,
  [PropertyType.Campaign]: SortAlphabeticalDirection,
}

const DirectionPicker = ({
  property,
  selectedDirection,
  onDirectionSelected,
}: {
  property: Property
  selectedDirection?: SortingDirection
  onDirectionSelected: (direction: SortingDirection) => void
}) => {
  const { isReadOnlyUser } = useHandbookReadOnlyUser()

  if (!selectedDirection) return null

  const directions = Object.keys(Directions[property.type])

  return (
    <DropdownTrigger popoverClassName={Styles.popover} placement="bottom-start">
      <Button
        variant="secondaryGrey"
        size="lg"
        withChevron="down"
        data-testid="direction-picker-trigger"
        isDisabled={isReadOnlyUser}
      >
        <span className={Styles.triggerText}>{Directions[property.type][selectedDirection]}</span>
      </Button>
      {(close) => (
        <Menu
          aria-label="Direction Picker Menu"
          data-testid="direction-picker-options"
          selectionMode="single"
          onAction={(value) => {
            onDirectionSelected(value as SortingDirection)
            close()
          }}
          selectedKeys={[selectedDirection]}
        >
          {directions.map((direction: string) => (
            <Item key={direction} textValue={Directions[property.type][direction as SortingDirection]}>
              {Directions[property.type][direction as SortingDirection]}
            </Item>
          ))}
        </Menu>
      )}
    </DropdownTrigger>
  )
}

export { DirectionPicker }
